import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Star, Plus, ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";

// Homepage product showcase: pulls only **parent** categories from the admin
// catalog and surfaces up to 8 products (4 per row on lg). A "Show all" CTA
// jumps to the dedicated /products page (carrying the active category if any).
const HOMEPAGE_PRODUCT_LIMIT = 8;

export default function ProductsSection() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allCategoriesRaw, setAllCategoriesRaw] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState("all"); // "all" | category.id
    const { addItem } = useCart();

    useEffect(() => {
        Promise.all([
            api.get("/products").then(({ data }) => data).catch(() => []),
            api.get("/categories").then(({ data }) => data).catch(() => []),
        ]).then(([prods, cats]) => {
            setProducts(prods);
            setAllCategoriesRaw(cats || []);
            // Only top-level (parent) categories show as pills on the homepage.
            setCategories((cats || []).filter((c) => !c.parent_id));
            setLoading(false);
        });
    }, []);

    const idsForParent = useMemo(() => {
        const byParent = new Map();
        for (const c of allCategoriesRaw) {
            const k = c.parent_id || "__root__";
            if (!byParent.has(k)) byParent.set(k, []);
            byParent.get(k).push(c.id);
        }
        const out = new Map();
        const walk = (rootId) => {
            const acc = new Set([rootId]);
            const stack = [rootId];
            while (stack.length) {
                const cur = stack.pop();
                for (const childId of (byParent.get(cur) || [])) {
                    if (!acc.has(childId)) {
                        acc.add(childId);
                        stack.push(childId);
                    }
                }
            }
            return acc;
        };
        for (const c of allCategoriesRaw) {
            if (!c.parent_id) out.set(c.id, walk(c.id));
        }
        return out;
    }, [allCategoriesRaw]);

    const filtered = useMemo(() => {
        if (active === "all") return products;
        const ids = idsForParent.get(active) || new Set([active]);
        // Collect the names of every category in the active sub-tree so we can
        // also match legacy products that only have the free-text `category`
        // string (CSV-imported / older data).
        const names = new Set(
            allCategoriesRaw.filter((c) => ids.has(c.id)).map((c) => c.name),
        );
        return products.filter((p) => {
            if (p.category_id && ids.has(p.category_id)) return true;
            if (p.category && names.has(p.category)) return true;
            return false;
        });
    }, [active, products, idsForParent, allCategoriesRaw]);

    const displayed = filtered.slice(0, HOMEPAGE_PRODUCT_LIMIT);
    const shopAllHref = active === "all" ? "/products" : `/products?category_id=${active}`;

    const handleAdd = (p) => {
        addItem(
            {
                id: p.id,
                kind: "product",
                name: p.name,
                price: p.price,
                image_url: p.image_url,
                meta: p.category,
            },
            1,
        );
        toast.success("Added to cart", { description: p.name });
    };

    return (
        <section
            id="products"
            className="py-20 md:py-28 bg-paper-alt"
            data-testid="products-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold tracking-wide uppercase">
                        Shop Essentials
                    </span>
                    <h2 className="mt-5 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-ink-900">
                        Crafted for your daily practice
                    </h2>
                    <p className="mt-4 text-lg text-ink-700">
                        Sustainable mats, supportive accessories, and movement-friendly
                        clothing — handpicked by our teachers.
                    </p>
                </div>

                {/* Parent-category filter pills (admin-managed) */}
                {categories.length > 0 && (
                    <div className="mt-10 flex flex-wrap justify-center gap-3" role="tablist">
                        <button
                            onClick={() => setActive("all")}
                            role="tab"
                            aria-selected={active === "all"}
                            className={`filter-pill border ${
                                active === "all"
                                    ? "bg-brand-500 text-white border-brand-500 shadow-cta"
                                    : "bg-white text-ink-700 border-gray-200 hover:border-brand-300 hover:text-brand-700"
                            }`}
                            data-testid="filter-all"
                        >
                            All
                        </button>
                        {categories.map((c) => {
                            const isActive = active === c.id;
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => setActive(c.id)}
                                    role="tab"
                                    aria-selected={isActive}
                                    className={`filter-pill border ${
                                        isActive
                                            ? "bg-brand-500 text-white border-brand-500 shadow-cta"
                                            : "bg-white text-ink-700 border-gray-200 hover:border-brand-300 hover:text-brand-700"
                                    }`}
                                    data-testid={`filter-${c.slug || c.id}`}
                                >
                                    {c.name}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Grid — up to 8 products, 4 per row on lg */}
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {loading
                        ? [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                              <div
                                  key={i}
                                  className="h-[420px] rounded-2xl bg-gray-100 animate-pulse"
                              />
                          ))
                        : displayed.map((p, idx) => (
                              <article
                                  key={p.id}
                                  className="group bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 transition-all duration-300 hover:shadow-brand hover:-translate-y-1 animate-fade-up flex flex-col"
                                  style={{ animationDelay: `${idx * 50}ms` }}
                                  data-testid={`product-card-${p.id}`}
                              >
                                  <div className="relative aspect-square overflow-hidden bg-paper-alt">
                                      <Link
                                          to={`/products/${p.id}`}
                                          aria-label={p.name}
                                          className="absolute inset-0"
                                          data-testid={`product-link-${p.id}`}
                                      >
                                          {p.image_url ? (
                                              <img
                                                  src={p.image_url}
                                                  alt={p.name}
                                                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                              />
                                          ) : (
                                              <span className="absolute inset-0 flex items-center justify-center text-ink-400 text-xs">No image</span>
                                          )}
                                      </Link>
                                      {p.category && (
                                          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-semibold uppercase tracking-wider text-ink-900">
                                              {p.category}
                                          </span>
                                      )}
                                      <button
                                          onClick={() => handleAdd(p)}
                                          className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-accent-500 hover:bg-accent-600 text-white flex items-center justify-center shadow-accent opacity-0 group-hover:opacity-100 transition-opacity translate-y-1 group-hover:translate-y-0"
                                          aria-label={`Add ${p.name} to cart`}
                                          data-testid={`add-to-cart-${p.id}`}
                                      >
                                          <Plus className="h-5 w-5" strokeWidth={2.4} />
                                      </button>
                                  </div>
                                  <div className="p-5 flex flex-col gap-3 flex-1">
                                      <Link
                                          to={`/products/${p.id}`}
                                          className="hover:text-brand-600 transition-colors"
                                      >
                                          <h3 className="font-heading text-base font-semibold text-ink-900 hover:text-brand-600 transition-colors line-clamp-1">
                                              {p.name}
                                          </h3>
                                      </Link>
                                      <p className="text-xs text-ink-700 line-clamp-2 leading-relaxed flex-1">
                                          {p.description}
                                      </p>
                                      <div className="flex items-center justify-between pt-1">
                                          <div className="flex items-center gap-1 text-xs text-ink-700">
                                              <Star className="h-3.5 w-3.5 fill-accent-400 text-accent-400" />
                                              <span className="font-semibold text-ink-900">
                                                  {p.rating?.toFixed(1) ?? "4.8"}
                                              </span>
                                          </div>
                                          <div className="font-heading text-lg font-semibold text-brand-600">
                                              ₹{p.price.toLocaleString("en-IN")}
                                          </div>
                                      </div>
                                      <Button
                                          onClick={() => handleAdd(p)}
                                          variant="outline"
                                          className="rounded-full border-brand-200 text-brand-700 hover:bg-brand-50 hover:text-brand-700 hover:border-brand-300 mt-1"
                                          data-testid={`add-button-${p.id}`}
                                      >
                                          Add to Cart
                                      </Button>
                                  </div>
                              </article>
                          ))}
                </div>

                {!loading && filtered.length === 0 && (
                    <div className="text-center py-16 text-ink-700">
                        No products in this category yet.
                    </div>
                )}

                {/* Show all → /products (carries the active category if any) */}
                {!loading && filtered.length > 0 && (
                    <div className="mt-12 flex justify-center">
                        <Link to={shopAllHref} data-testid="products-show-all-link">
                            <Button
                                className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-7 h-11 text-sm font-medium"
                                data-testid="products-show-all-btn"
                            >
                                Show all
                                {filtered.length > HOMEPAGE_PRODUCT_LIMIT && (
                                    <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 text-[11px] font-semibold">
                                        {filtered.length}
                                    </span>
                                )}
                                <ArrowRight className="h-4 w-4 ml-1.5" />
                            </Button>
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}
